import { useState, useEffect } from "react";
import { useActiveSite } from "../context/ActiveSiteContext";
import { useAuth } from "../context/AuthContext";
import { parseApiError } from "../utils/apiErrors";
import { Globe, Plus } from "lucide-react";

export default function CreateWorkspaceModal() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { workspaces, loading: workspaceLoading, createWorkspace } = useActiveSite();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  // Derive slug automatically from name
  useEffect(() => {
    const derived = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
    setSlug(derived);
  }, [name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Workspace name is required.");
      return;
    }
    if (!slug.trim()) {
      setError("A valid slug is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createWorkspace(name.trim(), slug.trim());
    } catch (err) {
      setError(parseApiError(err, "Failed to create workspace. Try another name."));
    } finally {
      setSaving(false);
    }
  };

  // Only render if logged in, auth check completed, not an admin, workspace list is loaded, and they have 0 workspaces
  if (!isAuthenticated || authLoading || isAdmin || workspaceLoading || workspaces.length > 0) {
    return null;
  }

  return (
    <div className="modal-overlay" style={{ backdropFilter: "blur(8px)", backgroundColor: "rgba(10, 10, 12, 0.85)" }}>
      <div
        className="modal-card"
        style={{
          maxWidth: "480px",
          width: "100%",
          padding: "32px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.5)",
          background: "linear-gradient(135deg, rgba(22, 22, 28, 0.95), rgba(15, 15, 20, 0.98))",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3b82f6",
            }}
          >
            <Globe size={28} />
          </div>
        </div>

        <h2 style={{ textAlign: "center", margin: "0 0 12px 0", fontSize: "24px", fontWeight: "600", color: "#f3f4f6" }}>
          Create Your Workspace
        </h2>
        <p
          style={{
            textAlign: "center",
            color: "#9ca3af",
            fontSize: "14px",
            lineHeight: "1.6",
            margin: "0 0 24px 0",
          }}
        >
          Welcome to Nova SEO! Set up a dedicated workspace to collaborate with team members, configure SEO projects, and manage monitoring schedules.
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="field-label" style={{ marginBottom: "18px", color: "#e5e7eb" }}>
            Workspace Name
            <input
              className="input"
              type="text"
              placeholder="e.g. Acme Corporation"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={saving}
              style={{
                marginTop: "6px",
                width: "100%",
                padding: "10px 14px",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#fff",
              }}
            />
          </div>

          <div className="field-label" style={{ marginBottom: "24px", color: "#e5e7eb" }}>
            Workspace URL Slug
            <input
              className="input"
              type="text"
              placeholder="e.g. acme-corporation"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
              disabled={saving}
              style={{
                marginTop: "6px",
                width: "100%",
                padding: "10px 14px",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: "6px",
                color: "#9ca3af",
              }}
            />
          </div>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={saving || !name.trim()}
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "15px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {saving ? "Creating Workspace..." : "Create Workspace"}
            {!saving && <Plus size={16} />}
          </button>
        </form>

        {error && (
          <div
            className="progress-error"
            style={{
              marginTop: "16px",
              padding: "12px",
              borderRadius: "6px",
              fontSize: "13px",
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              color: "#ef4444",
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
